import type { Season, SeasonCategory } from "./config";

export type RollingWindow = { start: Date; end: Date };

export function getRollingWindow(today: Date = new Date()): RollingWindow {
  const start = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
    ),
  );
  const end = new Date(start);
  end.setUTCFullYear(end.getUTCFullYear() + 1);
  return { start, end };
}

export type PositionedSeason = Season & {
  start: Date;
  end: Date;
  clippedStart: Date;
  clippedEnd: Date;
  leftPct: number;
  widthPct: number;
  startsBeforeWindow: boolean;
  endsAfterWindow: boolean;
};

function toUtcDate(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

export function seasonsInWindow(
  seasons: Season[],
  window: RollingWindow,
): PositionedSeason[] {
  const span = window.end.getTime() - window.start.getTime();
  const result: PositionedSeason[] = [];
  for (const season of seasons) {
    const start = toUtcDate(season.typicalStart);
    const end = toUtcDate(season.typicalEnd);
    if (end <= window.start || start >= window.end) continue;
    const clippedStart = start < window.start ? window.start : start;
    const clippedEnd = end > window.end ? window.end : end;
    const leftPct = ((clippedStart.getTime() - window.start.getTime()) / span) * 100;
    const widthPct = ((clippedEnd.getTime() - clippedStart.getTime()) / span) * 100;
    result.push({
      ...season,
      start,
      end,
      clippedStart,
      clippedEnd,
      leftPct,
      widthPct,
      startsBeforeWindow: start < window.start,
      endsAfterWindow: end > window.end,
    });
  }
  return result;
}

export function layoutRows(positioned: PositionedSeason[]): number[] {
  const order = positioned
    .map((p, i) => ({ p, i }))
    .sort((a, b) => a.p.clippedStart.getTime() - b.p.clippedStart.getTime());
  const rowEnds: number[] = [];
  const rows = new Array<number>(positioned.length);
  for (const { p, i } of order) {
    let placed = false;
    for (let r = 0; r < rowEnds.length; r++) {
      if (rowEnds[r] <= p.clippedStart.getTime()) {
        rows[i] = r;
        rowEnds[r] = p.clippedEnd.getTime();
        placed = true;
        break;
      }
    }
    if (!placed) {
      rows[i] = rowEnds.length;
      rowEnds.push(p.clippedEnd.getTime());
    }
  }
  return rows;
}

export const CATEGORY_COLOURS: Record<
  SeasonCategory,
  { band: string; bandAlt: string; text: string; label: string }
> = {
  winter: {
    band: "bg-[#4A6FA5]",
    bandAlt: "bg-[#6B8FC2]",
    text: "text-white",
    label: "Winter",
  },
  summer: {
    band: "bg-[#C17B5D]",
    bandAlt: "bg-[#1C8A99]",
    text: "text-white",
    label: "Summer",
  },
  festival: {
    band: "bg-[#8D6B8C]",
    bandAlt: "bg-[#A87FA6]",
    text: "text-white",
    label: "Festival",
  },
};

export function bandColourFor(category: SeasonCategory, slug: string): string {
  const alt = slug.charCodeAt(0) % 2 === 0;
  const c = CATEGORY_COLOURS[category];
  return alt ? c.bandAlt : c.band;
}
